import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { PermissionsService } from '../../../shared/services/permissions.service';
import { PermissionDTO } from '../../../shared/types/permission.types';

@Component({
  selector: 'app-permission-detail',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ModalComponent,
    PageHeaderComponent,
    DataStateComponent,
    ConfirmModalComponent,
  ],
  templateUrl: './permission-detail.component.html',
})
export class PermissionDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly permission = signal<PermissionDTO | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  // Manage policies modal
  readonly policiesOpen = signal(false);
  readonly policyAdding = signal(false);
  readonly policyRemoving = signal<string | null>(null);
  readonly policyError = signal<string | null>(null);
  readonly newPolicyCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1)],
  });

  private id = '';

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!this.id) {
      this.loadError.set('Identifiant de permission invalide.');
      this.loading.set(false);
      return;
    }
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.permissionsService
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (p) => { this.permission.set(p); this.loading.set(false); },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cette permission.'),
          );
        },
      });
  }

  // delete
  openDelete(): void { this.deleteError.set(null); this.deleteOpen.set(true); }
  closeDelete(): void { this.deleteOpen.set(false); }
  confirmDelete(): void {
    this.deleting.set(true);
    this.deleteError.set(null);
    this.permissionsService.delete(this.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteOpen.set(false);
        void this.router.navigate(['/permissions']);
      },
      error: (err: unknown) => {
        this.deleting.set(false);
        this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
      },
    });
  }

  // policies
  openPolicies(): void {
    this.policyError.set(null);
    this.newPolicyCtrl.reset('');
    this.policiesOpen.set(true);
  }
  closePolicies(): void {
    if (this.policyAdding() || this.policyRemoving()) return;
    this.policiesOpen.set(false);
  }
  addPolicy(): void {
    if (this.newPolicyCtrl.invalid || this.policyAdding()) return;
    const name = this.newPolicyCtrl.value.trim();
    if (!name) return;
    this.policyAdding.set(true);
    this.policyError.set(null);
    this.permissionsService
      .managePolicies(this.id, {
        permissionId: this.id,
        policyNamesOrIds: [name],
        operation: 'ADD',
      })
      .subscribe({
        next: () => {
          this.policyAdding.set(false);
          this.newPolicyCtrl.reset('');
          this.fetch();
        },
        error: (err: unknown) => {
          this.policyAdding.set(false);
          this.policyError.set(
            toErrorMessage(err, "L'ajout de la policy a échoué."),
          );
        },
      });
  }
  removePolicy(name: string): void {
    if (this.policyRemoving()) return;
    this.policyRemoving.set(name);
    this.policyError.set(null);
    this.permissionsService
      .managePolicies(this.id, {
        permissionId: this.id,
        policyNamesOrIds: [name],
        operation: 'REMOVE',
      })
      .subscribe({
        next: () => {
          this.policyRemoving.set(null);
          this.fetch();
        },
        error: (err: unknown) => {
          this.policyRemoving.set(null);
          this.policyError.set(
            toErrorMessage(err, 'Le retrait de la policy a échoué.'),
          );
        },
      });
  }
}
